import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import MakeModelSelector from '../components/shared/MakeModelSelector'
import { useAuth } from '../contexts/AuthContext'
import { intelliTrackClient } from '../lib/supabase'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'

interface ModelInfo {
  model_id: number
  make_name: string
  model_name: string
  display_name: string
}

export default function Home() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [marketModels, setMarketModels] = useState<ModelInfo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authLoading && user?.id) {
      fetchUserData()
    } else if (!authLoading && !user) {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading])

  const fetchUserData = async () => {
    try {
      setLoading(true)
      
      // Fetch user's market assignments
      const { data: assignments, error: assignmentsError } = await intelliTrackClient
        .from('market_assignments')
        .select('model_id')
        .eq('user_id', user!.id)

      if (assignmentsError) throw assignmentsError

      // Fetch market models if user has assignments
      if (assignments && assignments.length > 0) {
        const modelIds = assignments.map(a => a.model_id)
        const models = await ModelInfoCacheService.getModelsInfo(modelIds)
        // Sort by formatted make name and model name
        setMarketModels(models.sort((a, b) => {
          const aDisplay = `${capitalizeMakeName(a.make_name)} ${formatModelName(a.model_name)}`
          const bDisplay = `${capitalizeMakeName(b.make_name)} ${formatModelName(b.model_name)}`
          return aDisplay.localeCompare(bDisplay)
        }))
      }
    } catch (error) {
      // Error fetching user data
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-purple-950">
              Welcome to IntelliTrack
            </h1>
            <p className="mt-2 text-gray-600">
              This is your dashboard. You can navigate to different sections to view and manage data.
            </p>
          </div>

          {/* Make and Model Selector */}
          <MakeModelSelector />

          {/* Aircraft Markets Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Aircraft Markets
            </h2>
            
            {loading ? (
              <div className="text-gray-500">Loading your assigned markets...</div>
            ) : marketModels.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {marketModels.map((model) => {
                  const displayName = `${capitalizeMakeName(model.make_name)} ${formatModelName(model.model_name)}`
                  return (
                    <button
                      key={model.model_id}
                      onClick={() => navigate(`/market/${model.model_id}`)}
                      className="text-left px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-lg transition-colors duration-150 font-medium text-sm border border-purple-200 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    >
                      {displayName}
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 italic">
                No aircraft markets assigned. Contact your administrator to request access.
              </p>
            )}
          </div>
        </div>
      </main>
    </Layout>
  )
}